// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Polls a database ingest job until it succeeds, fails, or stalls.
//
// fetchJob:        async (jobId) => Response. Caller controls the URL builder
//                  so this module never references runtime endpoint constants.
// onStatus:        called with each job payload (or null) for UI updates.
// stallTimeoutMs:  how long without forward progress before we throw.
// intervalMs:      poll cadence between fetches.
// wait:            sleep function — overridable for tests.
// now:             time source — overridable for tests.

export async function waitForUploadIngestJob(jobId, {
  fetchJob,
  onStatus = () => {},
  stallTimeoutMs = 90000,
  intervalMs = 350,
  treatMissingAsDiscarded = false,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now = () => Date.now(),
} = {}) {
  if (!jobId) throw new Error("Upload did not return an ingest job id.");
  if (typeof fetchJob !== "function") throw new Error("fetchJob callable is required.");
  let lastJob = null;
  let lastCompleted = -1;
  let lastStep = "";
  let lastProgressAt = now();
  let consecutiveFetchFailures = 0;
  while (true) {
    let response;
    try {
      response = await fetchJob(jobId);
      consecutiveFetchFailures = 0;
    } catch (error) {
      // A long batch fires thousands of status polls; a single dropped
      // connection ("Failed to fetch") must not fail an upload whose ingest
      // is running fine server-side. Retry a few times before giving up.
      consecutiveFetchFailures += 1;
      if (consecutiveFetchFailures >= 5) {
        throw new Error(`Lost connection while waiting for upload processing: ${error?.message ?? error}`);
      }
      await wait(Math.max(intervalMs, 1000));
      continue;
    }
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      if (treatMissingAsDiscarded && response.status === 404) {
        // Preserve whatever summary the last successful poll saw (skip and
        // refusal counts) so a job deleted between polls still reports why.
        const discardedJob = {
          id: jobId,
          status: "completed",
          current_step: "discarded",
          progress_completed: 0,
          progress_total: 0,
          summary_json: {
            ...(lastJob?.summary_json ?? {}),
            discarded: true,
            discardReason: "ingest-job-not-found",
          },
        };
        onStatus(discardedJob);
        return discardedJob;
      }
      throw new Error(payload?.error ?? `Could not read upload ingest job ${jobId}.`);
    }
    const job = payload?.job;
    lastJob = job ?? lastJob;
    onStatus(job);
    if (job?.status === "completed") return job;
    if (job?.status === "failed") {
      throw new Error(job.error_message || "Upload processing failed.");
    }
    const completed = Number(job?.progress_completed ?? 0);
    const step = String(job?.current_step ?? "");
    if (completed !== lastCompleted || step !== lastStep) {
      lastCompleted = completed;
      lastStep = step;
      lastProgressAt = now();
    } else if (now() - lastProgressAt > stallTimeoutMs) {
      const seconds = Math.round(stallTimeoutMs / 1000);
      const stuckOn = step || lastJob?.status || "running";
      throw new Error(`Upload processing has been stuck on '${stuckOn}' for ${seconds}s. Try refreshing the flight list.`);
    }
    await wait(intervalMs);
  }
}
